/**
 * Bloque C: Evaluación musical
 * 
 * @author Ana Lizcano
 * @version 2.0.0
 */

import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { 
  BaseCard,
  ActionButton,
  ScrollableArea,
  FlexContainer,
  GridContainer
} from '../../styles/commonStyles';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { styled } from "@mui/material/styles";
import VideoPlayer from '../VideoPlayer';
import { loadDynamicVideos } from '../../data/blocks/blockC';

// Función para aleatorizar arrays usando Fisher-Yates shuffle
const shuffleArray = (array, seed) => {
  const shuffled = [...array];
  // Usar seed para generar números pseudo-aleatorios consistentes
  let random = () => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  };
  
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Función para generar seed único por usuario/sesión
const generateUserSeed = () => {
  // Usar timestamp + un número aleatorio para crear seed único
  const timestamp = Date.now();
  const randomNum = Math.floor(Math.random() * 10000);
  return timestamp + randomNum;
};

// Contenedor estándar para reproductores de video
const VideoContainer = styled(MDBox)(() => ({
  width: '100%',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Wrapper para reproductores de video con estado de selección visual (para comparaciones)
const VideoWrapper = styled(MDBox)(({ selected }) => ({
  border: `3px solid ${selected ? '#2196f3' : '#e0e0e0'}`,
  borderRadius: '16px',
  padding: '16px',
  backgroundColor: selected ? '#e3f2fd' : '#ffffff',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  boxShadow: selected ? '0 6px 20px rgba(33, 150, 243, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
  transform: selected ? 'scale(1.02)' : 'scale(1)',
  '&:hover': {
    borderColor: selected ? '#2196f3' : '#90caf9',
    backgroundColor: selected ? '#e3f2fd' : '#f3f9ff',
    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.2)',
    transform: 'scale(1.02)',
  },
}));

const BlockC = ({ question, response, onResponseChange }) => {
  const [dynamicQuestion, setDynamicQuestion] = useState(question);
  const [loading, setLoading] = useState(true);

  // Cargar videos dinámicamente al montar el componente
  useEffect(() => {
    const loadVideos = async () => {
      try {
        const updatedQuestion = await loadDynamicVideos();
        setDynamicQuestion({ ...question, ...updatedQuestion });
      } catch (error) {
        // Usar la pregunta original como fallback
        setDynamicQuestion(question);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, [question]);

  const responses = response || {};
  const currentScreen = useMemo(() => responses.currentScreen || 'individual', [responses.currentScreen]);
  const currentSongIndex = useMemo(() => responses.currentSongIndex || 0, [responses.currentSongIndex]);  
  const currentPairIndex = useMemo(() => responses.currentPairIndex || 0, [responses.currentPairIndex]);

  // Aleatorizar canciones y pares una sola vez por usuario
  const { shuffledSongs, shuffledPairs } = useMemo(() => {
    if (!dynamicQuestion.songs || !dynamicQuestion.pairs) {
      return { shuffledSongs: [], shuffledPairs: [] };
    }
    
    // Si ya existe un seed guardado, usarlo; sino generar uno nuevo
    let userSeed = responses.userSeed;
    if (!userSeed) {
      userSeed = generateUserSeed();
    }
    
    const shuffledSongs = shuffleArray(dynamicQuestion.songs, userSeed);
    const shuffledPairs = shuffleArray(dynamicQuestion.pairs, userSeed + 1000); // Offset para pares
    
    return { shuffledSongs, shuffledPairs };
  }, [dynamicQuestion.songs, dynamicQuestion.pairs, responses.userSeed]);

  // Inicializar el estado si no existe
  useEffect(() => {
    if (!responses.currentScreen || !responses.userSeed) {
      const userSeed = responses.userSeed || generateUserSeed();
      onResponseChange({
        ...responses,
        currentScreen: 'individual',
        currentSongIndex: 0,
        userSeed: userSeed
      });
    }
  }, [responses, onResponseChange]);

  if (loading) {
    return (
      <BaseCard>
        <MDBox p={3} textAlign="center">
          <MDTypography variant="h6" color="text">
            Cargando fragmentos musicales...
          </MDTypography>
        </MDBox>
      </BaseCard>
    );
  }

  if (!dynamicQuestion || dynamicQuestion.type !== 'music_test') return null;

  // Maneja respuestas de evaluación individual (me gusta/no me gusta)
  const handleIndividualResponse = (songId, liked) => {
    const newResponses = {
      ...responses,
      individual: {
        ...responses.individual,
        [songId]: liked
      }
    };
    onResponseChange(newResponses);
  };

  // Maneja respuestas de la fase de comparación (A vs B)
  const handleComparisonResponse = (pairId, choice) => {
    const newResponses = {
      ...responses,
      comparison: {
        ...responses.comparison,
        [pairId]: choice
      }
    };
    onResponseChange(newResponses);
  };

  // EVALUACIÓN INDIVIDUAL: Una canción por pantalla
  if (currentScreen === 'individual') {
    const currentSong = shuffledSongs[currentSongIndex];
    const userResponse = responses.individual?.[currentSong?.id];
    
    if (!currentSong) return null;

    return (
      <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Contenido principal */}
        <MDBox sx={{ flex: 1, padding: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <BaseCard variant="elevated" sx={{ padding: 3, textAlign: 'center', maxWidth: '800px', margin: '0 auto', width: '100%' }}>
            
            <VideoContainer>
              <VideoPlayer
                src={currentSong.videoUrl}
                fragmentDuration={20}
                maxPlays={2}
                onPlayCountChange={(count) => {
                  const newResponses = {
                    ...responses,
                    playCount: {
                      ...responses.playCount,
                      [currentSong.id]: count
                    }
                  };
                  onResponseChange(newResponses);
                }}
              />
            </VideoContainer>

            <MDTypography variant="h6" fontWeight="bold" textAlign="center" mb={3}>
              ¿Te gusta este fragmento musical?
            </MDTypography>

            <FlexContainer justify="center" gap={4}>
              <ActionButton
                variant="like"
                size="large"
                selected={userResponse === true}
                onClick={() => handleIndividualResponse(currentSong.id, true)}
              >
                ✅ Sí, me gusta
              </ActionButton>
              <ActionButton
                variant="dislike"
                size="large"
                selected={userResponse === false}
                onClick={() => handleIndividualResponse(currentSong.id, false)}
              >
                ❌ No me gusta
              </ActionButton>
            </FlexContainer>
          </BaseCard>
        </MDBox>

      </MDBox>
    );
  }

  // FASE DE COMPARACIÓN: Un par por pantalla
  if (currentScreen === 'comparison') {
    const currentPair = shuffledPairs[currentPairIndex];
    const userResponse = responses.comparison?.[currentPair?.id];
    
    if (!currentPair) return null;

    const songA = shuffledSongs.find(s => s.id === currentPair.songA);
    const songB = shuffledSongs.find(s => s.id === currentPair.songB);

    return (
      <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Contenido principal */}
        <MDBox sx={{ flex: 1, padding: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <BaseCard variant="elevated" sx={{ padding: 2, margin: '0 auto', maxWidth: '1200px', width: '100%' }}>
            <MDTypography variant="h4" fontWeight="bold" color="dark" mb={2.5} textAlign="center" sx={{ fontSize: '1.6rem' }}>
              ¿Cuál de estos dos fragmentos musicales prefieres?
            </MDTypography>

            <GridContainer columns={2} gap={2} sx={{ marginBottom: 2 }}>
              {/* Fragmento A */}
              <VideoWrapper 
                selected={userResponse === 'A'}
                onClick={() => handleComparisonResponse(currentPair.id, 'A')}
              >
                <MDTypography variant="body1" fontWeight="bold" mb={2} textAlign="center" color="primary">
                  Opción A
                </MDTypography>
                <VideoContainer>
                  <VideoPlayer
                    src={songA?.videoUrl}
                    fragmentDuration={20}
                    maxPlays={2}
                    onPlayCountChange={(count) => {
                      const newResponses = {
                        ...responses,
                        playCount: {
                          ...responses.playCount,
                          [`${songA.id}_comparison`]: count
                        }
                      };
                      onResponseChange(newResponses);
                    }}
                  />
                </VideoContainer>
              </VideoWrapper>

              {/* Fragmento B */}
              <VideoWrapper 
                selected={userResponse === 'B'}
                onClick={() => handleComparisonResponse(currentPair.id, 'B')}
              >
                <MDTypography variant="body1" fontWeight="bold" mb={2} textAlign="center" color="primary">
                  Opción B
                </MDTypography>
                <VideoContainer>
                  <VideoPlayer
                    src={songB?.videoUrl}
                    fragmentDuration={20}
                    maxPlays={2}
                    onPlayCountChange={(count) => {
                      const newResponses = {
                        ...responses,
                        playCount: {
                          ...responses.playCount,
                          [`${songB.id}_comparison`]: count
                        }
                      };
                      onResponseChange(newResponses);
                    }}
                  />
                </VideoContainer>
              </VideoWrapper>
            </GridContainer>

            <FlexContainer justify="center" gap={4} sx={{ mt: 2 }}>
              <ActionButton
                variant="primary"
                size="large"
                selected={userResponse === 'A'}
                onClick={() => handleComparisonResponse(currentPair.id, 'A')}
              >
                🔘 Prefiero la Opción A
              </ActionButton>
              <ActionButton
                variant="primary"
                size="large"
                selected={userResponse === 'B'}
                onClick={() => handleComparisonResponse(currentPair.id, 'B')}
              >
                🔘 Prefiero la Opción B
              </ActionButton>
            </FlexContainer>
          </BaseCard>
        </MDBox>

      </MDBox>
    );
  }

  // PANTALLA DE FINALIZACIÓN
  if (currentScreen === 'completed') {
    return (
      <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <BaseCard variant="success" sx={{ padding: 3, textAlign: 'center' }}>
          <MDTypography variant="h4" fontWeight="bold" color="success" mb={2}>
            ¡Evaluación Completada!
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={2}>
            Has evaluado todas las canciones exitosamente.
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Gracias por tu participación.
          </MDTypography>
        </BaseCard>
      </MDBox>
    );
  }

  return null;
};

BlockC.propTypes = {
  question: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    introText: PropTypes.string.isRequired,
    songs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      videoUrl: PropTypes.string.isRequired
    })).isRequired,
    pairs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      songA: PropTypes.string.isRequired,
      songB: PropTypes.string.isRequired
    })).isRequired
  }).isRequired,
  response: PropTypes.object,
  onResponseChange: PropTypes.func.isRequired
};

export default BlockC;